import * as Dialog from "@radix-ui/react-dialog";
import GlobeIcon from "@redotech/redo-icons/globe-02.svg";
import SearchIcon from "@redotech/redo-icons/search-md.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { useHandler } from "@redotech/react-util/hook";
import { memo, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { setLocale } from "./i18n";
import { localizationConfig } from "./localization-config";
import * as styles from "./locale-selector.module.css";
import { ALL_LOCALES, getLocaleName, type Locale } from "./locales";

interface LocaleSelectorProps {
  onLocaleChange?: (locale: string) => void;
}

export const LocaleSelector = memo(function LocaleSelector({
  onLocaleChange,
}: LocaleSelectorProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const handleOpenChange = useHandler((next: boolean) => {
    setOpen(next);
    if (!next) setQuery("");
  });

  const handleSelect = useHandler((locale: Locale) => {
    void setLocale(locale);
    onLocaleChange?.(locale);
    setOpen(false);
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    if (!q) return ALL_LOCALES;
    return ALL_LOCALES.filter(
      (l) => getLocaleName(l).toLocaleLowerCase().includes(q) || l.includes(q),
    );
  }, [query]);

  const currentPrimary = i18n.language.split("-")[0];

  if (!localizationConfig.isEnabled()) {
    return null;
  }

  return (
    <Dialog.Root onOpenChange={handleOpenChange} open={open}>
      <Dialog.Trigger asChild>
        <button
          aria-label={t("Select language")}
          className={styles.button}
          type="button"
        >
          <GlobeIcon className={styles.icon} height={16} width={16} />
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.content}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            searchRef.current?.focus();
          }}
        >
          <div className={styles.header}>
            <Dialog.Close asChild>
              <button
                aria-label={t("Close")}
                className={styles.closeButton}
                type="button"
              >
                <XCloseIcon height={16} width={16} />
              </button>
            </Dialog.Close>
            <Dialog.Title className={styles.title}>
              {t("Language")}
            </Dialog.Title>
            <span />
          </div>
          <Dialog.Description className={styles.visuallyHidden}>
            {t("Choose your preferred language")}
          </Dialog.Description>
          <div className={styles.body}>
            <div className={styles.searchWrapper}>
              <SearchIcon
                className={styles.searchIcon}
                height={16}
                width={16}
              />
              <input
                className={styles.searchInput}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("Search language")}
                ref={searchRef}
                type="search"
                value={query}
              />
            </div>
            {filtered.length === 0 ? (
              <div className={styles.empty}>{t("No languages found")}</div>
            ) : (
              <div className={styles.grid}>
                {filtered.map((locale) => {
                  const selected = currentPrimary === locale;
                  const className = selected
                    ? `${styles.option} ${styles.optionSelected}`
                    : styles.option;
                  return (
                    <button
                      className={className}
                      key={locale}
                      onClick={() => handleSelect(locale)}
                      type="button"
                    >
                      {getLocaleName(locale)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
});
